import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { LogOut, Menu, ChevronDown, Languages } from 'lucide-react';
import SidebarNav from '@/components/dashboard/SidebarNav';
import NotificationBell from '@/components/dashboard/NotificationBell';
import ThemeToggle from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { SUPPORTED_LANGUAGES, setLanguage, getLanguage } from '@/i18n';
import { translateRole } from '@/lib/i18nLabels';
import { toast } from 'sonner';

const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const Topbar = ({ mobileOpen, setMobileOpen }) => {
  const navigate = useNavigate();
  const { user, signOut, isAdmin } = useAuth();
  const { theme } = useTheme();
  // Hybrid = light root with DARK chrome: force the dark palette on the top bar
  // and the mobile nav sheet. Hybrid Dark already runs the root in `.dark`.
  const chromeDark = theme === 'hybrid';
  const { t, i18n } = useTranslation();
  const currentLang = i18n.resolvedLanguage || getLanguage();

  const meta = user?.user_metadata || {};
  const fullName =
    meta.full_name ||
    meta.name ||
    [meta.first_name, meta.last_name].filter(Boolean).join(' ').trim() ||
    user?.email?.split('@')[0] ||
    t('brand.roles.team_member');
  // Role label is translated at render time via brand.roles.* (Spanish/English
  // both honored). Falls back to a title-cased raw value if the role key
  // isn't recognized.
  const role = translateRole(t, meta.role || meta.title || 'operator');
  const initials = getInitials(fullName);

  const handleSignOut = async () => {
    await signOut();
    toast.success(t('auth.sign_out'));
    navigate('/login', { replace: true });
  };

  const handleLanguageChange = (code) => {
    setLanguage(code);
    toast.success(t('settings.language_changed'));
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-30 pt-safe border-b border-border bg-[hsl(var(--card))]/80 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--card))]/70',
        chromeDark && 'dark'
      )}
      data-testid="dashboard-topbar"
    >
      <div className="h-14 sm:h-16 px-3 sm:px-6 flex items-center justify-between gap-3">
        {/* LEFT: hamburger (mobile) + brand */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden h-11 w-11 shrink-0 -ml-1 text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
                aria-label={t('nav.open_aria')}
                data-testid="sidebar-open-button"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className={cn(
                'p-0 w-[300px] bg-[hsl(var(--sidebar))] border-r border-border',
                chromeDark && 'dark'
              )}
            >
              <SheetTitle className="sr-only">{t('common.navigation')}</SheetTitle>
              <SidebarNav
                idPrefix="sidebar-mobile"
                onNavigate={() => setMobileOpen(false)}
              />
            </SheetContent>
          </Sheet>

          <div className="flex items-center min-w-0">
            {/* Theme-aware header mark — light logo on cream, original in dark. */}
            <img
              src="/kocore-header-light.png"
              alt="K.O. CO+RE Systems"
              className="h-14 w-auto shrink-0 object-contain block dark:hidden"
              data-testid="topbar-brand-shield-light"
              onError={(e) => {
                if (e.currentTarget.src.endsWith('/kocore-header-light.png')) {
                  e.currentTarget.src = '/kocore-header.png';
                }
              }}
            />
            <img
              src="/kocore-header.png"
              alt="K.O. CO+RE Systems"
              className="h-14 w-auto shrink-0 object-contain hidden dark:block"
              data-testid="topbar-brand-shield"
            />
          </div>
        </div>

        {/* RIGHT: theme + user + signout */}
        <div className="flex items-center gap-2 sm:gap-3">
          <ThemeToggle variant="compact" className="hidden md:inline-flex" />
          {isAdmin ? <NotificationBell /> : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="group flex items-center gap-2 sm:gap-3 rounded-md pl-1.5 pr-2 py-1.5 hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 transition-colors"
                data-testid="user-menu-button"
                aria-label={t('nav.open_user_menu')}
              >
                <Avatar className="h-8 w-8 border border-[hsl(var(--primary))]/30">
                  <AvatarFallback className="bg-[hsl(var(--muted))] text-[hsl(var(--primary))] text-xs font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden sm:flex flex-col items-start min-w-0 max-w-[180px]">
                  <span
                    className="text-sm font-semibold text-foreground truncate w-full"
                    data-testid="topbar-user-name"
                  >
                    {fullName}
                  </span>
                  <span
                    className="text-xs text-muted-foreground truncate w-full"
                    data-testid="topbar-user-role"
                  >
                    {role}
                  </span>
                </div>
                <ChevronDown className="hidden sm:block h-4 w-4 text-muted-foreground group-hover:text-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-64 bg-[hsl(var(--card))] border-border"
            >
              <DropdownMenuLabel className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-foreground">{fullName}</span>
                <span className="text-xs text-muted-foreground truncate">
                  {user?.email}
                </span>
                <Badge
                  variant="secondary"
                  className="mt-1 w-fit bg-[hsl(var(--muted))] text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/25 hover:bg-[hsl(var(--muted))]"
                >
                  {role}
                </Badge>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-border" />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  className="cursor-pointer text-foreground focus:bg-[hsl(var(--muted))] focus:text-foreground"
                  data-testid="topbar-language-trigger"
                >
                  <Languages className="h-4 w-4 mr-2 text-muted-foreground" />
                  {t('settings.tab_language')}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent
                    className="bg-[hsl(var(--card))] border-border"
                  >
                    <DropdownMenuRadioGroup
                      value={currentLang}
                      onValueChange={handleLanguageChange}
                    >
                      {SUPPORTED_LANGUAGES.map((l) => (
                        <DropdownMenuRadioItem
                          key={l.code}
                          value={l.code}
                          className="cursor-pointer text-foreground focus:bg-[hsl(var(--muted))] focus:text-foreground"
                          data-testid={`topbar-lang-${l.code}`}
                        >
                          {l.label}
                          <span className="ml-2 text-xs text-muted-foreground">
                            {l.shortLabel}
                          </span>
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuSeparator className="bg-border" />
              <DropdownMenuItem
                onClick={handleSignOut}
                className="cursor-pointer text-foreground focus:bg-[hsl(var(--muted))] focus:text-foreground"
                data-testid="signout-button"
              >
                <LogOut className="h-4 w-4 mr-2 text-muted-foreground" />
                {t('auth.sign_out')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Topbar;
